import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// Redux
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import { getSuppliers, resetReducerSuppliers, useSupplierReducer } from "@/main/store/supplier";

// Services
import {
	getDefaultOptionDrop,
	getGuidScreenByRoute,
	getTranslateByKey,
	removeEmptyValues,
} from "@/main/services/utils";
import { userIsBodyshop, userIsInsurer, userIsSupplier } from "@/main/services/utilsUser";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import {
	formatDateToISOString,
	initalDateForm,
	onLoadDateRangePicker,
	validateFormDate,
} from "@/main/services/utilsDate";
import { VehicleAutoCompleteTypeEnum } from "@/main/services/enum/VehicleAutoCompleteTypeEnum";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { downloadFile } from "@/main/services/utilsFile";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";

// Types
import { KeyValue } from "@/main/models/common";
import {
	BodyShopFilterRequest,
	InsurerFilterRequest,
	ProcurementReportRequest,
	SupplierFilterRequest,
} from "@/main/models/request";
import { ProcurementReportResponse, VehicleAutoCompleteResponse } from "@/main/models/response";

// Api
import { controlApi, procurementReportApi } from "@/main/api";

// Components
import KogKeyboardDatePickerContainer from "@/main/components/shared/datepicker/keyboardDatePickerContainer";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import Box from "@/main/components/shared/box";
import { AreaButton } from "@/main/components/shared/styles";
import Button from "@/main/components/shared/button/button";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import VehicleAutoCompleteComponent from "@/main/components/shared/vehicle/VehicleAutoCompleteComponent";
import { withQueryparams } from "@/main/components/core/queryParams/queryparams";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import useFormQueryParams from "@/main/hooks/useformQueryParams";

// Components/Styles Local
import OrderReportGrid from "./components/orderReportGrid/orderReportGrid";

import * as S from "./styles";
import { useNavigate } from "react-router-dom";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { Controls } from "@/main/services/constants/constantsControl";

export const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

export interface IGridData {
	data: ProcurementReportResponse[];
	totalItems: number;
	isLoading: boolean;
}

function OrderReportScreen() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const countryReducer = useCountryReducer();
	const insurerReducer = useInsurerReducer();
	const bodyshopReducer = useBodyshopReducer();
	const supplierReducer = useSupplierReducer();
	const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultSearchOrderReportDaysInterval);

	const optionDefaultCountryDrop = getDefaultOptionDrop(countryReducer.countriesUser.data);
	const optionDefaultSupplierDrop = getDefaultOptionDrop(supplierReducer.suppliers.data);
	const optionDefaultInsurerDrop = getDefaultOptionDrop(insurerReducer.insurers.data);
	const optionDefaultBodyshopDrop = getDefaultOptionDrop(bodyshopReducer.bodyshops.data);

	const INITIAL_FORM_STATE = {
		country: null as KeyValue | null,
		bodyshop: null as KeyValue | null,
		insurer: null as KeyValue | null,
		supplier: null as KeyValue | null,
		startDate: initalDateForm(parameterRangeDays),
		finalDate: initalDateForm(),
		vehicleManufacturer: "" as string,
		vehicleModel: "" as string,
		subModel: "" as string,
		vehicle: "" as string,
	};
	type IOrderReportForm = typeof INITIAL_FORM_STATE;

	const [showFilters, setShowFilters] = useState<boolean>(true);
	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [loadingExportOrder, setLoadingExportOrder] = useState<boolean>(false);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasClear, setHasClear] = useState<boolean>(false);
	const [hasShowMore, setHasShowMore] = useState<boolean>(false);
	const [showSchedultButton, setShowScheduleButton] = useState<boolean>(false);
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [formFilters, setFormFilters] = useState<any | null>(null);
	const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);

	const {
		control,
		customHandleSubmit,
		setValue,
		setError,
		formState: { errors },
		reset,
		getValues,
		clearErrors,
		watch,
	} = useFormQueryParams<IOrderReportForm>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE,
		onSubmit: onSubmitSearch,
		mapDefaultValues: {
			bodyshop: bodyshopReducer.bodyshops,
			country: countryReducer.countriesUser,
			insurer: insurerReducer.insurers,
			supplier: supplierReducer.suppliers,
		},
	});
	const watcherCountry = watch("country");

	const hasBodyshopDefault = userIsBodyshop() && bodyshopReducer?.bodyshops?.data?.length === 1;

	const hasInsurerDefault = (userIsInsurer() || userIsBodyshop()) && insurerReducer?.insurers?.data?.length === 1;

	const isDisabledInsurersDrop = () => insurerReducer.insurers.data === null || hasInsurerDefault;
	const isDisabledBodyshopDrop = () => bodyshopReducer.bodyshops.data === null || hasBodyshopDefault;
	const isDisabledSupplierDrop = () => supplierReducer.suppliers.data === null || userIsSupplier();

	function onSubmitSearch(dataForm: IOrderReportForm) {
		validateFormDate(dataForm.startDate, dataForm.finalDate, parameterRangeDays);

		setPageIndex(PaginationEnum.PageIndexIntial);
		setLoadingSave(true);

		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});

		const request: ProcurementReportRequest = removeEmptyValues({
			guidCountry: String(dataForm.country?.key) || "",
			guidBodyShop: String(dataForm.bodyshop?.key) || "",
			guidInsurer: String(dataForm.insurer?.key) || "",
			guidSupplier: String(dataForm.supplier?.key) || "",
			initialDate: formatDateToISOString(dataForm.startDate),
			finalDate: formatDateToISOString(dataForm.finalDate),
			guidVehicleManufacturer: dataForm.vehicleManufacturer,
			guidVehicleSubModel: dataForm.subModel,
			guidVehicleModel: dataForm.vehicleModel,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		});

		setFormFilters(request);

		procurementReportApi
			.searchgrid(request)
			.then((res) => {
				setGridData({
					isLoading: false,
					totalItems: res.data.totalNumberOfRecords,
					data: res.data.totalNumberOfRecords > 0 ? res.data.results : [],
				});
				setHasShowMore(res.data.results.length < res.data.totalNumberOfRecords);
			})
			.finally(() => {
				setLoadingSave(false);
			});

		setShowFilters(!showFilters);
	}

	function onSubmitExportOrder(dataForm: IOrderReportForm) {
		if (!watcherCountry) {
			return setError("country", {
				type: "required",
				message: getTranslateByKey("information.countryRequired", true),
			});
		}

		validateFormDate(dataForm.startDate, dataForm.finalDate, parameterRangeDays);

		setLoadingExportOrder(true);

		const request: ProcurementReportRequest = removeEmptyValues({
			guidCountry: String(dataForm.country?.key) || "",
			guidBodyShop: String(dataForm.bodyshop?.key) || "",
			guidInsurer: String(dataForm.insurer?.key) || "",
			guidSupplier: String(dataForm.supplier?.key) || "",
			initialDate: formatDateToISOString(dataForm.startDate),
			finalDate: formatDateToISOString(dataForm.finalDate),
			guidVehicleManufacturer: dataForm.vehicleManufacturer,
			guidVehicleSubModel: dataForm.subModel,
			guidVehicleModel: dataForm.vehicleModel,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		});

		procurementReportApi
			.exportOrder(request)
			.then((res) => {
				const resData = res.data.result;
				downloadFile(resData, "OrderReport.xlsx");
			})
			.finally(() => {
				setLoadingExportOrder(false);
			});
	}

	function handleClickClear() {
		reset(INITIAL_FORM_STATE);
		setFormFilters(null);
		setHasClear(true);
		setPageIndex(PaginationEnum.PageIndexIntial);
		setHasShowMore(false);
		setIsLoadingInfiniteScroll(false);

		if (optionDefaultCountryDrop) setValue("country", optionDefaultCountryDrop);

		if (optionDefaultCountryDrop !== null) {
			callBodyshopApiDomain();
			callInsurerApiDomain();
			callSupplierApiDomain(optionDefaultCountryDrop);
		} else {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
		}

		clearErrors();
		const { firstDateResult, secondDateResult } = onLoadDateRangePicker(parameterRangeDays);
		setValue("startDate", firstDateResult);
		setValue("finalDate", secondDateResult);

		setGridData(INITIAL_GRID_STATE);
	}

	function handleFilters() {
		setShowFilters(!showFilters);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}

	function callInsurerApiDomain(companySelected: KeyValue | null = null, countrySelected: KeyValue | null = null) {
		// Clean fields & reducers
		dispatch(resetReducerInsurers());

		if (countrySelected !== null || optionDefaultCountryDrop !== null) {
			const request: InsurerFilterRequest = {
				...(companySelected && { guidBodyShop: String(companySelected.key) }),
				guidCountry: optionDefaultCountryDrop?.key
					? String(optionDefaultCountryDrop?.key)
					: String(countrySelected?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};

			dispatch(getInsurers(request));
		}
	}

	function callBodyshopApiDomain(insurerSelected: KeyValue | null = null, countrySelected: KeyValue | null = null) {
		// Clean fields & reducers
		dispatch(resetReducerBodyshops());

		if (countrySelected !== null || optionDefaultCountryDrop !== null) {
			const request: BodyShopFilterRequest = {
				...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
				guidCountry: optionDefaultCountryDrop?.key
					? String(optionDefaultCountryDrop?.key)
					: String(countrySelected?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getBodyshops(request));
		}
	}

	function callSupplierApiDomain(optionSelected: KeyValue | null) {
		// Clean fields & reducers
		setValue("supplier", null);
		dispatch(resetReducerSuppliers());

		if (optionSelected !== null) {
			const request: SupplierFilterRequest = {
				guidCountry: String(optionSelected.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getSuppliers(request));
		}
	}

	function existsInDrop(data: KeyValue[], nameField: any) {
		const fieldKeySelected = getValues(nameField)?.key;
		const existsInData = data.find((item) => item.key === fieldKeySelected);

		if (!existsInData) {
			setValue(nameField, null);
		}
	}

	async function getControlsByScheduleReportScreen() {
		const guidReportScheduleScreen = getGuidScreenByRoute(RoutesConstants.reportOrderReportSchedule);
		const {
			data: { result },
		} = await controlApi.getControlsByScreenApi(guidReportScheduleScreen);

		if (!result) return;

		const hasControlScheduleButton = result.find((item) => item.elementId === Controls.IdOrderReportScheduleButton);
		setShowScheduleButton(!!hasControlScheduleButton);
	}

	useEffect(() => {
		if (insurerReducer.insurers.data && !insurerReducer.insurers.isLoading) {
			const isTypeCompanyUserInsurer = userIsInsurer();
			existsInDrop(insurerReducer.insurers.data, "insurer");
			if (isTypeCompanyUserInsurer && optionDefaultInsurerDrop)
				callBodyshopApiDomain(optionDefaultInsurerDrop, watcherCountry);
		}
	}, [insurerReducer.insurers.data, insurerReducer.insurers.isLoading]);

	useEffect(() => {
		if (bodyshopReducer.bodyshops.data && !bodyshopReducer.bodyshops.isLoading) {
			const isTypeCompanyUserBodyshop = userIsBodyshop();
			existsInDrop(bodyshopReducer.bodyshops.data, "bodyshopBranch");
			if (isTypeCompanyUserBodyshop && optionDefaultBodyshopDrop)
				callInsurerApiDomain(optionDefaultBodyshopDrop, watcherCountry);
		}
	}, [bodyshopReducer.bodyshops.data, bodyshopReducer.bodyshops.isLoading]);

	useEffect(() => {
		if (optionDefaultCountryDrop) {
			callInsurerApiDomain(null, watcherCountry);
			callBodyshopApiDomain(null, watcherCountry);
			callSupplierApiDomain(optionDefaultCountryDrop);
		}
	}, [optionDefaultCountryDrop?.key]);

	useEffect(() => {
		dispatch(getCountriesUser());
		getControlsByScheduleReportScreen();

		return () => {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
		};
	}, []);

	// CONSTANTS LAYOUT
	const titleScreen = `${getTranslateByKey("module.report")} > ${getTranslateByKey("screen.orderReport")}`;

	return (
		<>
			<ContainerScreen hasBackground title={titleScreen}>
				<AreaButton>
					{showSchedultButton && (
						<Button
							onClick={() => navigate(RoutesConstants.reportOrderReportSchedule)}
							id="id-orderReport-button-scheduleReport"
						>
							{getTranslateByKey("screen.orderReportSchedule")}
						</Button>
					)}
					<Button
						onClick={() => onSubmitExportOrder(getValues())}
						id="id-orderReport-button-exportExcel"
						isLoading={loadingExportOrder}
					>
						{getTranslateByKey("information.exportExcel")}
					</Button>
					<Button onClick={handleFilters} id="id-orderReport-button-filters">
						{getTranslateByKey("information.filters")}
					</Button>
				</AreaButton>
				<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
					<form onSubmit={customHandleSubmit} noValidate>
						<S.RowInputs>
							<KogAutoCompleteControlled
								id="id-orderReport-autoComplete-country"
								nameCustom="country"
								label={getTranslateByKey("information.country")}
								options={countryReducer.countriesUser.data}
								isLoading={countryReducer.countriesUser.isLoading}
								rulesCustom={{ required: true }}
								errorMessageCustom={
									errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
								}
								setValueCustom={setValue}
								disabled={countryReducer.countriesUser.data == null || optionDefaultCountryDrop != null}
								control={control}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									callBodyshopApiDomain(null, optionSelected);
									callInsurerApiDomain(null, optionSelected);
									callSupplierApiDomain(optionSelected);
								}}
								isDefaultValueUnique
							/>

							<KogAutoCompleteControlled
								nameCustom="insurer"
								id="id-orderReport-input-insurer"
								isDefaultValueUnique={optionDefaultCountryDrop !== null && optionDefaultInsurerDrop !== null}
								setValueCustom={setValue}
								label={getTranslateByKey("information.insurer")}
								control={control}
								rulesCustom={{ required: false }}
								errorMessageCustom=""
								hasErrorCustom={false}
								options={userIsBodyshop() ? null : insurerReducer.insurers.data}
								isLoading={insurerReducer.insurers.isLoading}
								disabled={userIsBodyshop() || isDisabledInsurersDrop()}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									if (optionSelected === null) {
										if (optionDefaultBodyshopDrop != null) {
											callInsurerApiDomain(optionDefaultBodyshopDrop, watcherCountry);
										} else {
											callInsurerApiDomain(null, watcherCountry);
										}
									}
									callBodyshopApiDomain(optionSelected, watcherCountry);
								}}
							/>

							<KogAutoCompleteControlled
								nameCustom="bodyshop"
								id="id-orderReport-input-bodyshop"
								isDefaultValueUnique={userIsBodyshop() && optionDefaultCountryDrop !== null}
								setValueCustom={setValue}
								label={getTranslateByKey("information.bodyshopBranch")}
								control={control}
								rulesCustom={{ required: false }}
								options={bodyshopReducer.bodyshops.data}
								isLoading={bodyshopReducer.bodyshops.isLoading}
								disabled={isDisabledBodyshopDrop()}
								onChangeCustom={(optionSelected: KeyValue | null) => {
									const isTypeCompanyUserInsurer = userIsInsurer();

									if (optionSelected === null) callBodyshopApiDomain(null, watcherCountry);
									if (!isTypeCompanyUserInsurer) callInsurerApiDomain(optionSelected, watcherCountry);
								}}
							/>
						</S.RowInputs>
						<S.RowInputs>
							<KogAutoCompleteControlled
								label={getTranslateByKey("information.supplier")}
								control={control}
								nameCustom="supplier"
								isDefaultValueUnique={userIsSupplier() && optionDefaultSupplierDrop !== null}
								rulesCustom={{ required: false }}
								errorMessageCustom=""
								hasErrorCustom={false}
								options={supplierReducer.suppliers.data}
								isLoading={supplierReducer.suppliers.isLoading}
								onChangeCustom={() => {}}
								disabled={isDisabledSupplierDrop()}
								autoHighlightCustom={!!optionDefaultSupplierDrop}
								setValueCustom={setValue}
								id="id-orderReport-input-supplier"
							/>

							<KogKeyboardDatePickerContainer
								idScreen="id-orderReport"
								nameStartDate="startDate"
								nameFinalDate="finalDate"
								parameterRangeDays={parameterRangeDays}
								control={control}
							/>

							<VehicleAutoCompleteComponent
								hasClearValue={hasClear}
								setHasClearValue={setHasClear}
								handleChangeVehicle={(vehicle: VehicleAutoCompleteResponse) => {
									setValue("vehicleManufacturer", vehicle?.guidVehicleManufacturer);
									setValue("vehicleModel", vehicle?.guidVehicleModel || "");
									setValue("subModel", vehicle?.guidSubModel || "");
									setValue("vehicle", vehicle?.value);
								}}
								vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Partial}
							/>
						</S.RowInputs>

						<S.AreaButton>
							<SecondaryButton id="id-orderReport-button-search" type="submit" isLoading={loadingSave}>
								{getTranslateByKey("information.search")}
							</SecondaryButton>
							<SecondaryButton id="id-orderReport-button-clear" onClick={handleClickClear}>
								{getTranslateByKey("information.clear")}
							</SecondaryButton>
						</S.AreaButton>
					</form>
				</Box>

				<OrderReportGrid
					formFilters={formFilters}
					gridData={gridData}
					setGridData={setGridData}
					hasShowMore={hasShowMore}
					setHasShowMore={setHasShowMore}
					pageIndex={pageIndex}
					setPageIndex={setPageIndex}
					isLoadingInfiniteScroll={isLoadingInfiniteScroll}
					setIsLoadingInfiniteScroll={setIsLoadingInfiniteScroll}
				/>
			</ContainerScreen>
		</>
	);
}

export default withQueryparams(OrderReportScreen);
